/**
 * Prefetch-enabled Link Components
 * Automatically preloads route code and data on hover/focus
 * 
 * @module PrefetchLink
 */

import React, { useCallback, useRef, useEffect } from 'react';
import { Link, NavLink, type LinkProps, type NavLinkProps } from 'react-router-dom';
import { preloadRouteWithData, preloadRoute } from '../utils/routePreload';

interface PrefetchLinkProps extends LinkProps {
  /** Whether to prefetch data in addition to code (default: true) */
  prefetchData?: boolean;
  /** Delay before prefetching on hover in ms (default: 75) */
  prefetchDelay?: number;
  /** Whether to prefetch on visibility (default: false) */
  prefetchOnVisible?: boolean;
  /** Disable prefetching entirely (default: false) */
  disablePrefetch?: boolean;
}

/**
 * Enhanced Link component that prefetches route code and data on hover
 * 
 * @example
 * ```tsx
 * // Basic usage - prefetches on hover
 * <PrefetchLink to="/sales/performance">Performance</PrefetchLink>
 * 
 * // Prefetch only code, not data
 * <PrefetchLink to="/sales/pipeline" prefetchData={false}>Pipeline</PrefetchLink>
 * 
 * // Prefetch when visible in viewport
 * <PrefetchLink to="/hr/employees" prefetchOnVisible>HR Employees</PrefetchLink>
 * ```
 */
export const PrefetchLink = React.forwardRef<HTMLAnchorElement, PrefetchLinkProps>(
  function PrefetchLink(
    {
      to,
      prefetchData = false,
      prefetchDelay = 75,
      prefetchOnVisible = false,
      disablePrefetch = false,
      onMouseEnter,
      onFocus,
      children,
      ...props
    },
    ref
  ) {
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const prefetchedRef = useRef(false);
    const internalRef = useRef<HTMLAnchorElement | null>(null);
    
    // Combine refs
    const setRef = useCallback((element: HTMLAnchorElement | null) => {
      internalRef.current = element;
      if (typeof ref === 'function') {
        ref(element);
      } else if (ref) {
        ref.current = element;
      }
    }, [ref]);

    // Get the path from the `to` prop
    const getPath = useCallback(() => {
      if (typeof to === 'string') return to;
      if (typeof to === 'object' && to.pathname) return to.pathname;
      return null;
    }, [to]);

    const doPrefetch = useCallback(() => {
      if (prefetchedRef.current || disablePrefetch) return;
      
      const path = getPath();
      if (!path) return;

      prefetchedRef.current = true;
      
      if (prefetchData) {
        preloadRouteWithData(path);
      } else {
        preloadRoute(path);
      }
    }, [getPath, prefetchData, disablePrefetch]);

    const handleMouseEnter = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
      if (!disablePrefetch && !prefetchedRef.current) {
        timeoutRef.current = setTimeout(doPrefetch, prefetchDelay);
      }
      onMouseEnter?.(e);
    }, [doPrefetch, prefetchDelay, disablePrefetch, onMouseEnter]);

    const handleMouseLeave = useCallback(() => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    }, []);

    const handleFocus = useCallback((e: React.FocusEvent<HTMLAnchorElement>) => {
      if (!disablePrefetch && !prefetchedRef.current) {
        doPrefetch();
      }
      onFocus?.(e);
    }, [doPrefetch, disablePrefetch, onFocus]);

    // Prefetch on visibility
    useEffect(() => {
      if (!prefetchOnVisible || disablePrefetch || !internalRef.current) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !prefetchedRef.current) {
              doPrefetch();
              observer.disconnect();
            }
          });
        },
        { rootMargin: '100px' }
      );

      observer.observe(internalRef.current);

      return () => observer.disconnect();
    }, [prefetchOnVisible, disablePrefetch, doPrefetch]);

    // Cleanup timeout on unmount
    useEffect(() => {
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }, []);

    return (
      <Link
        ref={setRef}
        to={to}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleFocus}
        {...props}
      >
        {children}
      </Link>
    );
  }
);

PrefetchLink.displayName = 'PrefetchLink';

/**
 * Prefetch-enabled NavLink Component Props
 */
interface PrefetchNavLinkProps extends NavLinkProps {
  /** Whether to prefetch data in addition to code (default: true) */
  prefetchData?: boolean;
  /** Delay before prefetching on hover in ms (default: 75) */
  prefetchDelay?: number;
  /** Whether to prefetch on visibility (default: false) */
  prefetchOnVisible?: boolean;
  /** Disable prefetching entirely (default: false) */
  disablePrefetch?: boolean;
}

/**
 * Enhanced NavLink component that prefetches route code and data on hover
 * Supports all NavLink features including className function and isActive
 * 
 * @example
 * ```tsx
 * // Basic usage with active state styling
 * <PrefetchNavLink 
 *   to="/dashboard"
 *   className={({ isActive }) => isActive ? 'active' : ''}
 * >
 *   Dashboard
 * </PrefetchNavLink>
 * ```
 */
export const PrefetchNavLink = React.forwardRef<HTMLAnchorElement, PrefetchNavLinkProps>(
  function PrefetchNavLink(
    {
      to,
      prefetchData = false,
      prefetchDelay = 75,
      prefetchOnVisible = false,
      disablePrefetch = false,
      onMouseEnter,
      onFocus,
      children,
      ...props
    },
    ref
  ) {
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const prefetchedRef = useRef(false);
    const internalRef = useRef<HTMLAnchorElement | null>(null);

    // Combine refs
    const setRef = useCallback((element: HTMLAnchorElement | null) => {
      internalRef.current = element;
      if (typeof ref === 'function') {
        ref(element);
      } else if (ref) {
        ref.current = element;
      }
    }, [ref]);

    // Get the path from the `to` prop
    const getPath = useCallback(() => {
      if (typeof to === 'string') return to;
      if (typeof to === 'object' && to.pathname) return to.pathname;
      return null;
    }, [to]);

    const doPrefetch = useCallback(() => {
      if (prefetchedRef.current || disablePrefetch) return;

      const path = getPath();
      if (!path) return;

      prefetchedRef.current = true;

      if (prefetchData) {
        preloadRouteWithData(path);
      } else {
        preloadRoute(path);
      }
    }, [getPath, prefetchData, disablePrefetch]);

    const handleMouseEnter = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
      if (!disablePrefetch && !prefetchedRef.current) {
        timeoutRef.current = setTimeout(doPrefetch, prefetchDelay);
      }
      onMouseEnter?.(e);
    }, [doPrefetch, prefetchDelay, disablePrefetch, onMouseEnter]);

    const handleMouseLeave = useCallback(() => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    }, []);

    const handleFocus = useCallback((e: React.FocusEvent<HTMLAnchorElement>) => {
      if (!disablePrefetch && !prefetchedRef.current) {
        doPrefetch();
      }
      onFocus?.(e);
    }, [doPrefetch, disablePrefetch, onFocus]);

    // Prefetch on visibility
    useEffect(() => {
      if (!prefetchOnVisible || disablePrefetch || !internalRef.current) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !prefetchedRef.current) {
              doPrefetch();
              observer.disconnect();
            }
          });
        },
        { rootMargin: '100px' }
      );

      observer.observe(internalRef.current);

      return () => observer.disconnect();
    }, [prefetchOnVisible, disablePrefetch, doPrefetch]);

    // Cleanup timeout on unmount
    useEffect(() => {
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }, []);

    return (
      <NavLink
        ref={setRef}
        to={to}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleFocus}
        {...props}
      >
        {children}
      </NavLink>
    );
  }
);

PrefetchNavLink.displayName = 'PrefetchNavLink';

export default PrefetchLink;
